/**
 * Governance activity log — the append-only audit trail behind Argo's
 * "governed writes" promise.
 *
 * WHY this exists: agents never write silently. Every action is proposed,
 * approved or rejected, and executed under a human's eye, and every agent run
 * is a real event. Until now none of that was recorded — the Dashboard showed
 * "0 audit entries" even after agent runs, which turned the governance story
 * into a UI pattern rather than a control. This collection makes it real and
 * inspectable, and is what the trust-sensitive buyer is actually asking for.
 *
 * Append-only by contract: this module exposes logging + reads, never an
 * update or delete (Firestore create-only rules are a follow-up). Entries are
 * computed from real events and never seeded, so an empty log honestly means
 * nothing governed has happened yet — never a fabricated number.
 */

import { db, nowIso, uuid } from './store';
import type { ActivityEntry } from './types';

/** The Dashboard "governed activity" window: trailing 7 days. */
export const ACTIVITY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** A new entry before the store stamps its id + timestamp + chain hashes. */
export type NewActivity = Omit<ActivityEntry, 'id' | 'at' | 'hash' | 'prevHash'> & { at?: string };

// ---------------------------------------------------------------------------
// Tamper-evidence: hash-chained entries (the Decision Ledger's seal)
// ---------------------------------------------------------------------------
//
// Each entry carries `hash` = digest(canonical fields + prevHash) and
// `prevHash` = the previous entry's hash ('' for genesis). Editing any entry
// — or splicing in a foreign tenant's rows — breaks the chain, and
// verifyLedger() pinpoints where. Hashing is best-effort: a failure never
// blocks the append itself.

/**
 * Canonical, order-stable serialization of the fields the chain covers.
 * Missing optionals normalize to '' so the digest is deterministic.
 */
function canonicalFields(e: Pick<ActivityEntry, 'at' | 'event' | 'source' | 'kind' | 'summary' | 'subjectId'>, prevHash: string): string {
  return JSON.stringify([e.at, e.event, e.source, e.kind ?? '', e.summary, e.subjectId ?? '', prevHash]);
}

/** Tiny pure-JS FNV-1a (hex) — fallback when SubtleCrypto is unavailable. */
function fnv1aHex(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    // 32-bit FNV prime multiply via shifts, keeping the math in uint32.
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return 'fnv1a:' + h.toString(16).padStart(8, '0');
}

/**
 * Stable, SYNCHRONOUS chain digest of one entry + its predecessor's hash.
 * Deliberately sync + fast (FNV-1a): an async SHA-256 per append was slow
 * enough to make the parallel test suite flaky, and for a local ledger what
 * matters is chain integrity (reorder/edit detection). Cryptographic-grade
 * tamper resistance comes from the server-side append-only enforcement
 * (Firestore create-only rules, multi-tenant), not a client hash. Deterministic
 * across browser + node.
 */
function hashEntry(
  e: Pick<ActivityEntry, 'at' | 'event' | 'source' | 'kind' | 'summary' | 'subjectId'>,
  prevHash: string,
): string {
  return fnv1aHex(canonicalFields(e, prevHash));
}

/** The hash of the latest chained entry ('' when the ledger is empty). */
async function latestHash(): Promise<string> {
  const all = await listActivity(); // newest-first
  // Skip legacy pre-chain entries (no hash) so old data can't wedge the chain.
  const latest = all.find((e) => typeof e.hash === 'string' && e.hash !== '');
  return latest?.hash ?? '';
}

/**
 * Append one governance event. Call sites treat this as best-effort (wrapped
 * in a catch) so recording an event can never break the action or run it
 * records. Returns the written entry.
 *
 * Tamper-evidence: the entry is hash-chained to the current latest entry
 * (genesis prevHash = ''). Hashing is itself best-effort — if it fails the
 * entry is still appended, just unsealed, preserving the original contract.
 */
export async function logActivity(entry: NewActivity): Promise<ActivityEntry> {
  const { at, ...rest } = entry;
  const record: ActivityEntry = { id: uuid(), at: at ?? nowIso(), ...rest };
  try {
    const prevHash = await latestHash();
    record.prevHash = prevHash;
    record.hash = hashEntry(record, prevHash);
  } catch {
    // Best-effort: a hashing failure must never break the append.
    delete record.prevHash;
    delete record.hash;
  }
  await db.add('activity', record);
  return record;
}

/**
 * Recompute the hash chain oldest→newest and confirm (a) each entry's
 * prevHash matches the prior entry's hash and (b) each entry's stored hash
 * matches a fresh digest of its fields — so both edits and splices surface.
 * Legacy entries without a hash are skipped (they predate the chain).
 * Returns ok:false + the id of the first entry where the chain breaks.
 */
export async function verifyLedger(): Promise<{ ok: boolean; brokenAt?: string }> {
  const rows = (await listActivity()).slice().reverse(); // oldest-first
  let prev = '';
  for (const e of rows) {
    if (typeof e.hash !== 'string' || e.hash === '') continue; // pre-chain entry
    if ((e.prevHash ?? '') !== prev) return { ok: false, brokenAt: e.id };
    const expected = hashEntry(e, prev);
    if (e.hash !== expected) return { ok: false, brokenAt: e.id };
    prev = e.hash;
  }
  return { ok: true };
}

/** All entries, newest-first. */
export async function listActivity(): Promise<ActivityEntry[]> {
  const all = await db.list('activity');
  return [...all].sort((a, b) => (a.at < b.at ? 1 : -1));
}

/** Count of entries within the trailing window (default 7 days). */
export async function recentActivityCount(
  windowMs: number = ACTIVITY_WINDOW_MS,
  now: number = Date.now(),
): Promise<number> {
  const all = await db.list('activity');
  const cutoff = now - windowMs;
  return all.filter((e) => Date.parse(e.at) >= cutoff).length;
}

/**
 * Quote a CSV field only when it needs it (comma, quote, or newline), doubling
 * embedded quotes per RFC 4180. Missing optionals export as empty columns —
 * never the string "undefined".
 */
function csvField(value: string | undefined): string {
  const v = value ?? '';
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/**
 * Export the full ledger, newest-first, as an audit-friendly CSV string.
 * Pure over listActivity(): one header line, then one line per entry
 * (columns: at, event, source, kind, summary, subjectId, hash — the hash
 * column makes the exported audit pack independently verifiable against the
 * chain). Read-only — exporting can never mutate the append-only log it
 * reports on.
 */
export async function exportLedger(): Promise<string> {
  const rows = await listActivity();
  const header = 'at,event,source,kind,summary,subjectId,hash';
  const lines = rows.map((e) =>
    [e.at, e.event, e.source, e.kind, e.summary, e.subjectId, e.hash].map(csvField).join(','),
  );
  return [header, ...lines].join('\n');
}

/** Subscribe to the activity log (newest-first). Returns an unsubscribe fn. */
export function subscribeActivity(fn: (rows: ActivityEntry[]) => void): () => void {
  return db.subscribe('activity', (rows) =>
    fn([...rows].sort((a, b) => (a.at < b.at ? 1 : -1))),
  );
}
